import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { equipmentService } from '@/services/equipment.service';
import { SkeletonPage } from '@/components/skeletons';

export function ScanPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['scan', token],
    queryFn: () => equipmentService.scanByToken(token!),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (data) {
      navigate(`/equipment/${data.id}`, { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) return <SkeletonPage />;

  if (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <FontAwesomeIcon icon={faCircleExclamation} className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-foreground">
          {status === 404 ? 'QR Code Not Found' : 'Access Denied'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {status === 404
            ? 'This QR code does not match any registered equipment.'
            : 'You do not have permission to scan QR codes.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        <FontAwesomeIcon icon={faQrcode} className="h-8 w-8 text-primary" />
      </div>
      <p className="text-sm text-muted-foreground">Resolving equipment...</p>
    </div>
  );
}
